import { Link, useLocation } from "wouter";
import { useClerk } from "@clerk/react";
import { clearRheumHubLocalStorage } from "@/lib/local-storage-keys";
import { useEffect } from "react";
import { GlobalSearch } from "@/components/ui/global-search";
import { DashboardHeader } from "@/components/layout/dashboard-header";
import {
  LayoutDashboard,
  Activity,
  Dumbbell,
  ClipboardList,
  Waves,
  HandMetal,
  Heart,
  BookOpen,
  MessageSquare,
  LogOut,
  Settings,
  GraduationCap,
  Shield,
  Star,
  TrendingUp,
  Brain,
  Monitor,
  Award,
  Globe,
  AlertTriangle,
  Users,
  BarChart3,
  CalendarDays,
} from "lucide-react";
import { LastSignInBanner } from "@/components/ui/last-signin-banner";
import { useIsAdmin } from "@/hooks/use-is-admin";
import { SessionNoticeBanner } from "@/components/ui/session-notice-banner";
import { HcpVerificationBanner } from "@/components/ui/hcp-verification-banner";
import { LegalFooter } from "@/components/layout/legal-footer";

export function PhysioLayout({ children, title }: { children: React.ReactNode; title: string }) {
  const [location] = useLocation();
  const { signOut } = useClerk();
  const isAdmin = useIsAdmin();

  useEffect(() => {
    document.title = `${title} | RheumHub`;
    return () => { document.title = "RheumHub"; };
  }, [title]);

  const clinicalItems = [
    { href: "/physio", label: "Dashboard", icon: LayoutDashboard },
    { href: "/physio/exercise-protocols", label: "Exercise & Rehab Protocols", icon: Dumbbell },
    { href: "/physio/patient-leaflets", label: "Patient Exercise Leaflets", icon: Globe },
    { href: "/physio/outcome-measures", label: "Outcome Measures", icon: ClipboardList },
    { href: "/physio/hydrotherapy", label: "Hydrotherapy", icon: Waves },
    { href: "/physio/manual-therapy", label: "Manual Therapy", icon: HandMetal },
    { href: "/physio/condition-protocols", label: "Condition-Specific Protocols", icon: Heart },
  ];

  const guidelinesItems = [
    { href: "/physio/guidelines", label: "CSP & NICE Guidelines", icon: BookOpen },
  ];

  const cpdItems = [
    { href: "/physio/learn", label: "Learning Hub", icon: BookOpen },
    { href: "/physio/cpd/portfolio", label: "Portfolio Tracker", icon: BarChart3 },
    { href: "/physio/cpd", label: "CPD Activity Log", icon: GraduationCap },
    { href: "/physio/cpd/revalidation", label: "HCPC Revalidation", icon: Shield },
    { href: "/physio/cpd/csp-framework", label: "CSP CPD Framework", icon: BookOpen },
    { href: "/physio/cpd/app-portfolio", label: "APP Portfolio", icon: Star },
    { href: "/physio/cpd/acpr", label: "ACPR Specialist", icon: Award },
    { href: "/physio/cpd/icsp", label: "iCSP Learning Log", icon: Monitor },
    { href: "/physio/cpd/reflection", label: "Reflection Tool", icon: Brain },
  ];

  const careerItems = [
    { href: "/physio/career", label: "Career Pathways", icon: TrendingUp },
  ];

  const eventsItems = [
    { href: "/physio/events", label: "Events", icon: CalendarDays },
  ];

  const professionalItems = [
    { href: "/physio/chat", label: "AI Clinical Assistant", icon: MessageSquare },
  ];

  const governanceItems = [
    { href: "/hcp/safety-report", label: "Report Safety Concern", icon: AlertTriangle },
    { href: "/physio/settings", label: "Your Data", icon: Settings },
  ];

  const isActive = (href: string) =>
    location === href || (location.startsWith(href + "/") && href !== "/physio");

  const renderNavItem = (item: { href: string; label: string; icon: React.ElementType }) => {
    const active = isActive(item.href);
    return (
      <Link
        key={item.href}
        href={item.href}
        className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] font-medium transition-colors ${
          active
            ? "bg-[#67e8f9]/15 text-[#67e8f9]"
            : "hover:bg-white/10 hover:text-white"
        }`}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <item.icon className={`h-4 w-4 shrink-0 ${active ? "text-[#67e8f9]" : "text-white/35"}`} />
        {item.label}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <SessionNoticeBanner />
      <LastSignInBanner />
      <HcpVerificationBanner />
      <div className="flex flex-1">
        <aside className="w-64 bg-[#00505e] text-white/75 flex flex-col fixed inset-y-0 z-20">
          <div className="p-6 flex items-center gap-3 text-white">
            <Activity className="h-6 w-6 text-[#67e8f9]" />
            <span className="text-xl font-bold tracking-tight">RheumHub</span>
          </div>

          <div className="px-6 py-2 text-xs font-semibold uppercase tracking-wider text-[#67e8f9]/60">
            Physiotherapy Portal
          </div>

          <nav className="sidebar-nav flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
            <div className="pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                Clinical Tools
              </div>
            </div>
            {clinicalItems.map((item) => renderNavItem(item))}

            <div className="pt-2.5 pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                Guidelines & Resources
              </div>
            </div>
            {guidelinesItems.map((item) => renderNavItem(item))}

            <div className="pt-2.5 pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                CPD Portfolio
              </div>
            </div>
            {cpdItems.map((item) => renderNavItem(item))}

            <div className="pt-2.5 pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                Career Development
              </div>
            </div>
            {careerItems.map((item) => renderNavItem(item))}

            <div className="pt-2.5 pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                Events
              </div>
            </div>
            {eventsItems.map((item) => renderNavItem(item))}

            <div className="pt-2.5 pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                Professional
              </div>
            </div>
            {professionalItems.map((item) => renderNavItem(item))}

            <div className="pt-2.5 pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                Governance
              </div>
            </div>
            {governanceItems.map((item) => renderNavItem(item))}
          </nav>

          <div className="p-3 border-t border-white/10 space-y-0.5">
            {isAdmin && (
              <Link
                href="/admin"
                className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] font-medium transition-colors ${
                  location.startsWith("/admin")
                    ? "bg-[#7bb8ff]/15 text-[#7bb8ff]"
                    : "hover:bg-white/10 hover:text-white text-white/35"
                }`}
              >
                <Users className="h-4 w-4 shrink-0" />
                Admin Panel
              </Link>
            )}
            <button
              onClick={() => { clearRheumHubLocalStorage(); signOut({ redirectUrl: "/" }); }}
              className="flex w-full items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] font-medium hover:bg-white/10 transition-colors text-white/35 hover:text-white"
              data-testid="nav-signout"
            >
              <LogOut className="h-4 w-4 shrink-0" />
              Sign out
            </button>
          </div>
        </aside>

        <main className="flex-1 pl-64 flex flex-col min-h-screen">
          <DashboardHeader title={title} profileLink="/physio/profile" />
          <div className="flex-1 p-8">
            <div className="max-w-6xl mx-auto">
              {children}
            </div>
          </div>
          <LegalFooter
            copyright="© RheumHub · For UK physiotherapists only · AI responses do not constitute clinical advice · Grounded in CSP, NICE, ACPR"
            extraLinks={[{ label: "Clinical Safety", href: "/clinical-safety" }]}
          />
        </main>
      </div>
    </div>
  );
}
